import { useState } from "react";
import { auth } from "@/lib/firebase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, XCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { apiClient } from '@/services/api/client';

export default function SetRolePage() {
  const { t } = useLanguage();
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("buyer");
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const currentUser = auth.currentUser;

  const fillMyUid = () => {
    if (currentUser) {
      setUid(currentUser.uid);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setLoading(true);
    try {
      // Get Firebase token for auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) throw new Error("Not authenticated");
      const token = await firebaseUser.getIdToken();

      // Call edge function which uses service role to bypass RLS
      const { data } = await apiClient.post('/auth/assign-role',
  { uid, role },
  { headers: { Authorization: `Bearer ${token}` } }
);
setMsg({ type: "success", text: data?.message || `Successfully assigned role "${role}" to user ${uid}` });
      setUid("");
    } catch (e: any) {
      setMsg({ 
        type: "error", 
        text: e.message || "An error occurred" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1D314D] via-[#2E4663] to-[#0f172a] p-6">
      <div className="container max-w-2xl mx-auto">
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Assigner un rôle utilisateur</CardTitle>
            <CardDescription className="text-white/70">
              Attribuez des rôles aux utilisateurs dans le système
            </CardDescription>
          </CardHeader>
          <CardContent>
            {currentUser && (
              <Alert className="mb-6 bg-white/5 border-white/20">
                <AlertDescription className="text-white/90">
                  Connecté en tant que: <strong>{currentUser.email}</strong>
                  <br />
                  Votre UID: <code className="text-xs bg-black/30 px-2 py-1 rounded">{currentUser.uid}</code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fillMyUid}
                    className="ml-4"
                  >
                    Utiliser mon UID
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="uid" className="text-white">UID utilisateur</Label>
                <Input
                  id="uid"
                  value={uid}
                  onChange={(e) => setUid(e.target.value)}
                  placeholder="Collez l'UID de l'utilisateur"
                  required
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-white">Rôle</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger id="role" className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                    <SelectItem value="broker">Broker</SelectItem>
                    <SelectItem value="lender">Lender</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "En cours..." : "Assigner le rôle"}
              </Button>
            </form>

            {msg && (
              <Alert className="mt-6" variant={msg.type === "error" ? "destructive" : "default"}>
                {msg.type === "success" ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertDescription>{msg.text}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
